import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Trash2, Phone, Mail, ArrowLeft, Inbox } from "lucide-react";
import SEO from "@/components/SEO";
import { listEnquiries, enquiryStats, updateEnquiry, deleteEnquiry } from "@/lib/api";

function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-forest/10 p-6">
      <div className="font-display text-5xl text-brand-forest">{value}</div>
      <div className="font-mono text-xs uppercase tracking-[0.2em] text-brand-muted mt-2">{label}</div>
    </div>
  );
}

export default function Admin() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([listEnquiries(), enquiryStats()]);
      setRows(e);
      setStats(s);
    } catch {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStatus = async (row) => {
    const next = row.status === "new" ? "contacted" : "new";
    try {
      await updateEnquiry(row.id, next);
      setRows((r) => r.map((x) => (x.id === row.id ? { ...x, status: next } : x)));
      enquiryStats().then(setStats);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await deleteEnquiry(id);
      setRows((r) => r.filter((x) => x.id !== id));
      enquiryStats().then(setStats);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-brand-bone">
      <SEO title="Admin · Enquiries" description="Enquiry dashboard" path="admin" />
      <header className="border-b border-brand-forest/10 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl text-brand-forest">Enquiries Dashboard</h1>
            <Link to="/" className="font-mono text-xs uppercase tracking-wide text-brand-muted inline-flex items-center gap-1 hover:text-brand-forest">
              <ArrowLeft size={12} /> Back to site
            </Link>
          </div>
          <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-full border border-brand-forest/20 px-5 py-2.5 text-brand-forest font-medium hover:bg-brand-forest hover:text-white transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-10">
        <div className="grid grid-cols-3 gap-4 md:gap-6 mb-10 max-w-2xl">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="New" value={stats.new} />
          <StatCard label="Contacted" value={stats.contacted} />
        </div>

        {rows.length === 0 && !loading ? (
          <div className="text-center py-24 text-brand-muted">
            <Inbox size={48} className="mx-auto opacity-40" />
            <p className="mt-4 font-display text-2xl text-brand-forest">No enquiries yet</p>
            <p className="mt-1">Submissions from the contact form will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-enquiry-list">
            {rows.map((r) => (
              <div key={r.id} data-testid={`enquiry-row-${r.id}`} className="bg-white rounded-2xl border border-brand-forest/10 p-6 flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="font-display text-xl text-brand-forest">{r.full_name}</div>
                    {r.business_name && <div className="text-sm text-brand-muted">{r.business_name}</div>}
                    <div className="text-sm text-brand-muted">{r.city}</div>
                  </div>
                  <div className="text-sm text-brand-muted space-y-1">
                    <a href={`tel:${r.mobile}`} className="flex items-center gap-2 hover:text-brand-forest"><Phone size={13} /> {r.mobile}</a>
                    {r.email && <a href={`mailto:${r.email}`} className="flex items-center gap-2 hover:text-brand-forest"><Mail size={13} /> {r.email}</a>}
                    <div className="font-mono text-[11px] uppercase tracking-wide">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-brand-forest font-medium">{r.product}{r.quantity ? ` · ${r.quantity}` : ""}</div>
                    {r.message && <div className="text-brand-muted mt-1 line-clamp-3">{r.message}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`toggle-status-${r.id}`}
                    onClick={() => toggleStatus(r)}
                    className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-wide transition-colors ${
                      r.status === "new" ? "bg-brand-terra/15 text-brand-terra" : "bg-brand-leaf/15 text-brand-leaf"
                    }`}
                  >
                    {r.status}
                  </button>
                  <button data-testid={`delete-${r.id}`} onClick={() => remove(r.id)} className="h-9 w-9 rounded-full flex items-center justify-center text-brand-muted hover:bg-destructive hover:text-white transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
